import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { ApmcFormValues, ListApmcsParams } from './schema';

const ROOT_KEY = 'apmcs';
const listKey = (params: ListApmcsParams) =>
  [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;
const optionsKey = [ROOT_KEY, 'options'] as const;
const pickerOptionsKey = [ROOT_KEY, 'pickerOptions'] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useApmcs(params: ListApmcsParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listApmcs(params),
    placeholderData: (prev) => prev,
  });
}

export function useApmc(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getApmc(id as string),
    enabled: !!id,
  });
}

export function useApmcOptions() {
  return useQuery({
    queryKey: optionsKey,
    queryFn: () => api.getApmcOptions(),
    staleTime: 60 * 60 * 1000,
  });
}

/** Rich active-list with state + district for M:N pickers (closure scope picker etc.).
 *  Lives in the apmcs feature (not marketClosures) so APMC mutations can
 *  invalidate it. Aggressively cached — APMC catalog changes rarely. */
export function useApmcPickerOptions() {
  return useQuery({
    queryKey: pickerOptionsKey,
    queryFn: () => api.getApmcPickerOptions(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateApmc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ApmcFormValues) => api.createApmc(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      queryClient.invalidateQueries({ queryKey: pickerOptionsKey });
      toast.success('APMC created');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to create APMC')),
  });
}

export function useUpdateApmc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: ApmcFormValues }) =>
      api.updateApmc(id, values),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      queryClient.invalidateQueries({ queryKey: pickerOptionsKey });
      toast.success('APMC updated');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to update APMC')),
  });
}
